import { useCallback, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import axios from 'axios';

export const useSettingsAPI = (showToast) => {
    const [isLoading, setIsLoading] = useState(false);
    const [settings, setSettings] = useState({});

    const API_BASE = window.HavenlyticsData?.hvnlyNabApi?.url || '/wp-json/hvnlynab/v1';
    const API_NONCE = window.HavenlyticsData?.hvnlyNabApi?.nonce || '';
    const SETTINGS_ENDPOINT = `${API_BASE}/plugin-settings`;

    const AXIOS_CONFIG = {
        headers: {
            'X-WP-Nonce': API_NONCE,
            'Content-Type': 'application/json'
        }
    };

    /**
     * Load all settings
     */
    const loadAllSettings = useCallback(async () => {
        // console.log('🔄 [API] loadAllSettings - Starting...');
        setIsLoading(true);
        try {
            const response = await axios.get(SETTINGS_ENDPOINT, AXIOS_CONFIG);
            if (response.data?.success) {
                setSettings(response.data.data);
                return response.data.data;
            }
            throw new Error(response.data?.message || 'Failed to load settings');
        } catch (error) {
            // console.error('❌ [API] loadAllSettings - Error:', error);
            showToast?.('error', __('Error', 'havenlytics'), __('Failed to load settings', 'havenlytics'));
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    /**
     * Save settings by group
     */
    const saveSettingsGroup = useCallback(async (group, data) => {
        // console.log(`💾 [API] saveSettingsGroup - Group: ${group}`);
        setIsLoading(true);
        try {
            const response = await axios.post(`${SETTINGS_ENDPOINT}/${group}`, data, AXIOS_CONFIG);
            if (response.data?.success) {
                setSettings(prev => ({ ...prev, [group]: response.data.data }));
                // REMOVED: showToast from here - let component handle it
                return { success: true, message: response.data.message, data: response.data.data };
            }
            throw new Error(response.data?.message || 'Failed to save settings');
        } catch (error) {
            // console.error(`❌ [API] saveSettingsGroup - Error for ${group}:`, error);
            showToast?.('error', __('Error', 'havenlytics'), error.response?.data?.message || `Failed to save ${group} settings`);
            return { success: false, message: error.response?.data?.message || `Failed to save ${group} settings` };
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    /**
     * Reset settings by group
     */
    const resetSettingsGroup = useCallback(async (group) => {
        // console.log(`🔄 [API] resetSettingsGroup - Group: ${group}`);
        setIsLoading(true);
        try {
            const response = await axios.post(`${SETTINGS_ENDPOINT}/${group}/reset`, {}, AXIOS_CONFIG);
            if (response.data?.success) {
                setSettings(prev => ({ ...prev, [group]: response.data.data }));
                // REMOVED: showToast from here - let component handle it
                return { success: true, message: response.data.message, data: response.data.data };
            }
            throw new Error(response.data?.message || 'Failed to reset settings');
        } catch (error) {
            // console.error(`❌ [API] resetSettingsGroup - Error for ${group}:`, error);
            showToast?.('error', __('Error', 'havenlytics'), error.response?.data?.message || `Failed to reset ${group} settings`);
            return { success: false, message: error.response?.data?.message || `Failed to reset ${group} settings` };
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    /**
     * Save all settings
     */
    const saveAllSettings = useCallback(async (allData) => {
        setIsLoading(true);
        try {
            const payload = { tabs: allData };
            const response = await axios.post(`${SETTINGS_ENDPOINT}/save-all`, payload, AXIOS_CONFIG);
            if (response.data?.success) {
                setSettings(response.data.data);
                return { success: true, message: response.data.message, data: response.data.data };
            }
            throw new Error(response.data?.message || 'Failed to save settings');
        } catch (error) {
            // console.error('❌ [API] saveAllSettings - Error:', error);
            showToast?.('error', __('Error', 'havenlytics'), __('Failed to save all settings', 'havenlytics'));
            return { success: false, message: __('Failed to save all settings', 'havenlytics') };
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    /**
     * Reset all settings
     */
    const resetAllSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(`${SETTINGS_ENDPOINT}/reset-all`, {}, AXIOS_CONFIG);
            if (response.data?.success) {
                setSettings(response.data.data);
                return { success: true, message: response.data.message, data: response.data.data };
            }
            throw new Error(response.data?.message || 'Failed to reset settings');
        } catch (error) {
            // console.error('❌ [API] resetAllSettings - Error:', error);
            showToast?.('error', __('Error', 'havenlytics'), error.response?.data?.message || __('Failed to reset all settings', 'havenlytics'));
            return { success: false, message: error.response?.data?.message || __('Failed to reset all settings', 'havenlytics') };
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    return {
        settings,
        isLoading,
        loadAllSettings,
        saveSettingsGroup,
        resetSettingsGroup,
        saveAllSettings,
        resetAllSettings
    };
};